import type { ActivityType } from './enums';

/**
 * The realtime protocol.
 *
 * Shared by the Durable Object and the browser client, so the two cannot drift about
 * message shapes — a mismatch there is invisible until a live update silently stops
 * arriving.
 */

/** One persisted board event, as broadcast to subscribers. */
export interface BoardEventMessage {
  /** Monotonic per board. The client's resume cursor. */
  seq: number;
  type: ActivityType;
  /** Who caused it, so the client can suppress the echo of its own change. */
  actorId: string | null;
  /**
   * Which browser tab caused it.
   *
   * Echo suppression keys on this, not on `actorId`. Suppressing by user id was too
   * blunt: it also ignored a person's change on their *other* tabs, which should update.
   * The originating tab set an optimistic update and must ignore the echo; every other
   * tab — same user or not — should apply it. Not persisted; attached to the live
   * message only, and absent on a replayed event (there is no live tab to attribute).
   */
  clientId?: string | null;
  payload: Record<string, unknown>;
  createdAt: number;
}

/** Header a browser tab sends on every mutation to identify itself for echo suppression. */
export const HEADER_CLIENT_ID = 'X-Client-Id';

export interface PresenceEntry {
  userId: string;
  name: string;
  avatarUrl: string | null;
}

// ─────────────────────────────────────────────────────────── server → client

export type RealtimeServerMessage =
  /** Sent immediately on connect: the current cursor and who else is here. */
  | { type: 'ready'; seq: number; presence: PresenceEntry[] }
  /** One or more persisted events, always in ascending `seq` order. */
  | { type: 'events'; events: BoardEventMessage[] }
  | { type: 'presence'; presence: PresenceEntry[] }
  /**
   * The client is too far behind to replay — refetch the board.
   *
   * Past a few hundred missed events it is cheaper to reload the snapshot than to
   * apply them one at a time, and a client offline for days may be beyond the
   * retention window entirely.
   */
  | { type: 'resync'; seq: number; reason: 'gap_too_large' | 'retention' }
  | { type: 'pong' };

// ─────────────────────────────────────────────────────────── client → server

export type RealtimeClientMessage =
  /** Heartbeat. Keeps intermediaries from closing an idle connection. */
  | { type: 'ping' }
  /** Sent after reconnecting, to ask for everything missed since `since`. */
  | { type: 'resume'; since: number };

// ────────────────────────────────────────────────────────────────── tunables

/**
 * How many events a single replay will deliver.
 *
 * Beyond this the server answers `resync` instead. The cutoff exists because applying
 * 500 individual patches is slower and more error-prone than one snapshot fetch.
 */
export const REALTIME_MAX_REPLAY = 200;

/**
 * Client heartbeat interval.
 *
 * 25 seconds is under the 30-second idle timeout most proxies apply. Messages on an
 * open socket do not count against the Worker request budget, so this is effectively
 * free — only the initial upgrade is billed as a request.
 */
export const REALTIME_HEARTBEAT_MS = 25_000;

/** Close code used when the server drops a connection because access was revoked. */
export const REALTIME_CLOSE_UNAUTHORISED = 4001;
/** Close code used when the board was deleted underneath the subscriber. */
export const REALTIME_CLOSE_GONE = 4004;

export function isServerMessage(value: unknown): value is RealtimeServerMessage {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { type?: unknown }).type === 'string'
  );
}
